import { BaseApiService } from "./baseApi";

interface BulkEmailParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
}

interface BulkEmailRecipient {
  data: any;
  id: string;
  name: string;
  email: string;
  phone?: string;
  status: string;
}

interface BulkEmailTemplate {
  data: any;
  id: string;
  name: string;
  subject: string;
  content: string;
  type?: string;
}

interface SendBulkEmailData {
  recipients: string[];
  subject: string;
  message: string;
  templateId?: string;
}

export class BulkEmailApiService extends BaseApiService {
  // Recipients
  async getRecipients(params: BulkEmailParams = {}): Promise<{
    data: any;
    recipients: BulkEmailRecipient[];
    total: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.status) queryParams.append("status", params.status);

    const endpoint = `/admin/bulk-email/recipients${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    return this.get(endpoint);
  }

  async addRecipient(
    recipient: Partial<BulkEmailRecipient>
  ): Promise<BulkEmailRecipient> {
    return this.post<BulkEmailRecipient>(
      "/admin/bulk-email/recipients",
      recipient
    );
  }

  async updateRecipient(
    id: string,
    recipient: Partial<BulkEmailRecipient>
  ): Promise<BulkEmailRecipient> {
    return this.put<BulkEmailRecipient>(
      `/admin/bulk-email/recipients/${id}`,
      recipient
    );
  }

  async deleteRecipient(id: string): Promise<void> {
    return this.delete<void>(`/admin/bulk-email/recipients/${id}`);
  }

  async uploadRecipientsCsv(file: File): Promise<{
    success: boolean;
    message: string;
    processedCount: number;
  }> {
    return this.upload<{
      success: boolean;
      message: string;
      processedCount: number;
    }>("/admin/bulk-email/upload-recipients", file);
  }

  // Templates
  async getTemplates(): Promise<{
    templates: BulkEmailTemplate[];
    total: number;
    totalPages: number;
  }> {
    const response = await this.get<{
      success: boolean;
      data: {
        templates: BulkEmailTemplate[];
        total: number;
        totalPages: number;
      };
    }>("/admin/bulk-email/templates");
    return response.data;
  }

  async createTemplate(
    template: Partial<BulkEmailTemplate>
  ): Promise<BulkEmailTemplate> {
    const response = await this.post<{
      success: boolean;
      data: BulkEmailTemplate;
    }>("/admin/bulk-email/templates", template);
    return response.data;
  }

  async updateTemplate(
    id: string,
    template: Partial<BulkEmailTemplate>
  ): Promise<BulkEmailTemplate> {
    const response = await this.put<{
      success: boolean;
      data: BulkEmailTemplate;
    }>(`/admin/bulk-email/templates/${id}`, template);
    return response.data;
  }

  async deleteTemplate(id: string): Promise<void> {
    return this.delete<void>(`/admin/bulk-email/templates/${id}`);
  }

  // Email sending
  async sendBulkEmail(data: SendBulkEmailData): Promise<{
    success: boolean;
    message: string;
    sentCount: number;
  }> {
    return this.post<{
      success: boolean;
      message: string;
      sentCount: number;
    }>("/admin/bulk-email/send", data);
  }

  // Stats
  async getEmailStats(filters?: any): Promise<any> {
    const queryParams = new URLSearchParams();
    if (filters?.timeFilter)
      queryParams.append("timeFilter", filters.timeFilter);
    if (filters?.customDateRange?.startDate)
      queryParams.append("startDate", filters.customDateRange.startDate);
    if (filters?.customDateRange?.endDate)
      queryParams.append("endDate", filters.customDateRange.endDate);

    const endpoint = `/admin/bulk-email/stats${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    return this.get(endpoint);
  }

  async getEmailHistory(
    params: {
      page?: number;
      limit?: number;
      startDate?: string;
      endDate?: string;
    } = {}
  ): Promise<{
    data: any;
    history: any[];
    total: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);

    const endpoint = `/admin/bulk-email/history${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    return this.get(endpoint);
  }

  // Groups
  async getGroups(): Promise<any[]> {
    return this.get<any[]>("/admin/bulk-email/groups");
  }

  async createGroup(groupData: {
    name: string;
    description: string;
    recipients: any[];
  }): Promise<any> {
    return this.post<any>("/admin/bulk-email/groups", groupData);
  }

  async updateGroup(
    id: string,
    groupData: { name: string; description: string; recipients: any[] }
  ): Promise<any> {
    return this.put<any>(`/admin/bulk-email/groups/${id}`, groupData);
  }

  async deleteGroup(id: string): Promise<void> {
    return this.delete<void>(`/admin/bulk-email/groups/${id}`);
  }
}
